/**
 * hooks/useProviderConnection/mapErrorToReason.ts
 * Error-to-reason mapping logic
 *
 * Pure function that maps unknown errors to SyncInitReason with optional retry timing.
 * Checks for structured SyncError first, falls back to error message patterns.
 */

import type { SyncInitReason } from '@/types/sync';
import type { SyncError } from '@/types/sync/SyncProvider';

/**
 * Maps errors to SyncInitReason.
 * Checks for structured SyncError first, falls back to error message patterns.
 *
 * Rationale for mappings:
 * - TIMEOUT/ABORTED: Explicit error messages from withTimeout
 * - OFFLINE: Network unavailable (check navigator.onLine)
 * - USER_CANCELLED: User explicitly cancelled (DOMException.AbortError or picker cancel)
 * - TOKEN_REVOKED/TOKEN_EXPIRED: HTTP 401/403/419/440 responses
 * - PROVIDER_OUTAGE: HTTP 429/5xx responses (may include retryAfterMs)
 * - DIRECTORY_INVALID: Local provider 404 or NotFoundError
 * - PERMISSION_DENIED: Browser denied permission (File System Access API)
 *
 * @param error - Error from provider operation
 * @returns Mapped SyncInitReason and optional retryAfterMs
 *
 * @example
 * ```ts
 * const { reason, retryAfterMs } = mapErrorToReason(error);
 * if (reason === 'PROVIDER_OUTAGE' && retryAfterMs) {
 *   console.log(`Retry after ${retryAfterMs}ms`);
 * }
 * ```
 */
export function mapErrorToReason(
  error: unknown
): { reason: SyncInitReason; retryAfterMs?: number } {
  // Check for structured SyncError first (preferred)
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    'provider' in error
  ) {
    const syncError = error as SyncError;

    // Edge function codes (from google-drive-proxy) - map to user-facing reasons
    // These aren't in SyncErrorCode but flow through createSyncError(data.code)
    const code = syncError.code as string;
    if (code === 'TRANSIENT' || code === 'PROVIDER_RATE_LIMITED') {
      return { reason: 'PROVIDER_OUTAGE' };
    }
    if (code === 'FOLDER_MISSING') {
      return { reason: 'DIRECTORY_INVALID' };
    }
    if (code === 'BAD_REQUEST') {
      return { reason: 'UNKNOWN_ERROR' };
    }

    // Map SyncErrorCode to SyncInitReason
    switch (syncError.code) {
      case 'NOT_CONNECTED':
        return { reason: 'TOKEN_EXPIRED' };

      case 'PERMISSION_DENIED':
        // Check details for 401/403 (recoverable) vs true permission denial
        if (
          syncError.details &&
          typeof syncError.details === 'object' &&
          'status' in syncError.details
        ) {
          const status = (syncError.details as { status: number }).status;
          if (status === 401 || status === 403) {
            return { reason: 'TOKEN_REVOKED' };
          }
        }
        return { reason: 'PERMISSION_DENIED' };

      case 'FILE_NOT_FOUND':
        return { reason: 'DIRECTORY_INVALID' };

      case 'NETWORK_ERROR':
        // Check for rate limiting (429) or outage (5xx)
        if (
          syncError.details &&
          typeof syncError.details === 'object' &&
          'status' in syncError.details
        ) {
          const status = (syncError.details as { status: number }).status;
          if (status === 429) {
            // Try to extract Retry-After header
            const retryAfterMs =
              'retryAfterMs' in syncError.details &&
              typeof (syncError.details as { retryAfterMs?: number })
                .retryAfterMs === 'number'
                ? ((syncError.details as { retryAfterMs: number })
                    .retryAfterMs as number)
                : undefined;
            return retryAfterMs
              ? { reason: 'PROVIDER_OUTAGE', retryAfterMs }
              : { reason: 'PROVIDER_OUTAGE' };
          }
          if (status >= 500) {
            return { reason: 'PROVIDER_OUTAGE' };
          }
        }
        return { reason: 'OFFLINE' };

      case 'QUOTA_EXCEEDED':
      case 'INVALID_FILE':
      case 'CONFLICT_DETECTED':
      case 'UNKNOWN_ERROR':
      default:
        return { reason: 'UNKNOWN_ERROR' };
    }
  }

  // Fall back to error message patterns
  if (error instanceof Error) {
    const message = error.message;

    // Explicit timeout/abort from withTimeout
    if (message === 'TIMEOUT') {
      return { reason: 'TIMEOUT' };
    }
    if (message === 'ABORTED') {
      return { reason: 'ABORTED' };
    }

    // User cancelled picker (File System Access API)
    if (
      error.name === 'AbortError' ||
      message.includes('user aborted') ||
      message.includes('USER_CANCELLED')
    ) {
      return { reason: 'USER_CANCELLED' };
    }

    // OAuth consent denied (user clicked "Deny" on Google consent screen)
    if (message.includes('ACCESS_DENIED')) {
      return { reason: 'ACCESS_DENIED' };
    }

    // OAuth scope denied (user denied file access in granular consent)
    if (message.includes('INSUFFICIENT_SCOPES')) {
      return { reason: 'INSUFFICIENT_SCOPES' };
    }

    // OAuth server error (Google outage, temporary server issue)
    if (message.includes('PROVIDER_OUTAGE')) {
      return { reason: 'PROVIDER_OUTAGE' };
    }

    // Offline detection
    if (message.includes('OFFLINE')) {
      return { reason: 'OFFLINE' };
    }

    // Token errors (401/403 = revoked, 419/440 = expired)
    if (
      message.includes('401') ||
      message.includes('403') ||
      message.includes('unauthorized') ||
      message.includes('forbidden')
    ) {
      return { reason: 'TOKEN_REVOKED' };
    }
    if (
      message.includes('419') ||
      message.includes('440') ||
      message.includes('token expired')
    ) {
      return { reason: 'TOKEN_EXPIRED' };
    }

    // Rate limiting (429)
    if (message.includes('429') || message.includes('rate limit')) {
      // Try to extract retryAfterMs from error message (e.g., "retry in 5 seconds")
      const match = message.match(/retry.*?(\d+)/i);
      const retryAfterMs = match?.[1] ? parseInt(match[1], 10) * 1000 : undefined;
      return retryAfterMs
        ? { reason: 'PROVIDER_OUTAGE', retryAfterMs }
        : { reason: 'PROVIDER_OUTAGE' };
    }

    // Server errors (5xx)
    if (message.includes('5') && /5\d{2}/.test(message)) {
      return { reason: 'PROVIDER_OUTAGE' };
    }

    // Directory/file errors (404, NotFoundError)
    if (
      message.includes('404') ||
      message.includes('not found') ||
      message.includes('NotFoundError')
    ) {
      return { reason: 'DIRECTORY_INVALID' };
    }

    // Permission denied (File System Access API)
    if (
      message.includes('NotAllowedError') ||
      message.includes('SecurityError') ||
      message.includes('permission denied')
    ) {
      return { reason: 'PERMISSION_DENIED' };
    }
  }

  // Default fallback
  return { reason: 'UNKNOWN_ERROR' };
}
